import { IconContext } from "@phosphor-icons/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useMemo } from "react";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";
import { registerAllActions } from "~/actions/definitions";
import type { ApiClient } from "~/api/client";
import { ApiProvider } from "~/api/provider";
import { NavigationProvider } from "~/foundation/components/navigation";
import { TooltipProvider } from "~/foundation/components/Tooltip";
import { i18n } from "~/foundation/i18n";
import { depths } from "~/foundation/styles/depths";
import { useApplyTheme } from "~/foundation/theme/useTheme";
import { type AppRouter, createAppRouter } from "~/routes";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 10_000,
      },
    },
  });
}

export interface AppProps {
  api: ApiClient;
  queryClient?: QueryClient;
  router?: AppRouter;
}

export function App({ api, queryClient, router }: AppProps) {
  const client = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);
  const appRouter = useMemo(
    () => router ?? createAppRouter({ queryClient: client, api }),
    [router, client, api],
  );

  // Foundation components render links without knowing which router the app uses.
  const navigate = useMemo(
    () => (to: string) => {
      void appRouter.navigate({ to: to as never });
    },
    [appRouter],
  );

  // Idempotent: re-registering replaces by id rather than duplicating.
  useMemo(() => registerAllActions(), []);
  useApplyTheme();

  return (
    /*
     * Phosphor's default `regular` weight reads thin at UI sizes, which is most of why the
     * chrome looked washed out next to the platform. Setting it once here rather than per
     * icon means no call site has to remember.
     */
    <IconContext.Provider value={{ weight: "bold" }}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={client}>
          <ApiProvider api={api}>
            <NavigationProvider navigate={navigate}>
              <TooltipProvider>
                <RouterProvider router={appRouter} />
                <Toaster
                  position="bottom-right"
                  style={{ zIndex: depths.toast }}
                  toastOptions={{
                    classNames: {
                      toast:
                        "bg-menu-bg! text-text! border-divider! shadow-menu! font-sans! rounded-md!",
                      description: "text-text-secondary!",
                      actionButton: "bg-accent! text-accent-text!",
                      cancelButton: "bg-bg-secondary! text-text!",
                    },
                  }}
                />
              </TooltipProvider>
            </NavigationProvider>
          </ApiProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </IconContext.Provider>
  );
}
